/**
 * 📊 «تحلیل بازار» — گزارش روزانه از حرف‌های واقعی گروه‌ها و پست‌های کانال‌ها.
 *
 * این تب جواب یک سؤال است: «فردا چه کار کنم؟» اول چک‌لیست کارها (قابل تیک‌زدن)،
 * بعد شواهد: چه می‌خواهند، چه محصولی کم داریم، از چه شاکی‌اند، رقبا چه کردند و
 * نقل‌قول‌های واقعی. ستون کنار، تاریخچهٔ روزها.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import NewspaperRoundedIcon from '@mui/icons-material/NewspaperRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import userbotService, {
  InsightActionStatus, InsightSource, UserbotInsight, UserbotInsightAction, UserbotInsightRow,
} from '../../services/userbotService';
import {
  EmptyHint, INTENT_FA, IconAction, Insights, Kpis, Loading, Pill, SectionCard, SubHead, Tag, UB,
  errMsg, fa, faDate, relTime,
} from './kit';
import type { Tone } from './kit';

const PRIORITY_FA: Record<string, { label: string; color: string }> = {
  high: { label: 'زیاد', color: UB.bad },
  medium: { label: 'متوسط', color: UB.warn },
  low: { label: 'کم', color: UB.muted },
};

const CATEGORY_FA: Record<string, string> = {
  product: 'محصول', site: 'سایت', content: 'محتوا', pricing: 'قیمت‌گذاری', support: 'پشتیبانی', marketing: 'بازاریابی',
};

/**
 * دو منبع، دو جنسِ داده: گروه‌ها می‌گویند مردم چه می‌خواهند، کانال‌ها می‌گویند
 * بازار چه عرضه می‌کند و به چه قیمتی. تحلیلشان جداست چون تصمیمی که از هرکدام
 * درمی‌آید فرق دارد.
 */
const SOURCES: Array<{ key: InsightSource; label: string; icon: React.ReactElement; hint: string }> = [
  { key: 'groups', label: 'گروه‌ها', icon: <GroupsRoundedIcon />, hint: 'مردم چه می‌خواهند و از چه شاکی‌اند' },
  { key: 'channels', label: 'کانال‌ها', icon: <CampaignOutlinedIcon />, hint: 'بازار چه عرضه می‌کند، با چه خبری و چه قیمتی' },
];

const STATUS_META: Record<InsightActionStatus, { label: string; color: string }> = {
  todo: { label: 'انجام نشده', color: UB.muted },
  doing: { label: 'در حال انجام', color: UB.info },
  done: { label: 'انجام شد', color: UB.ok },
  dropped: { label: 'کنار گذاشته شد', color: UB.bad },
};

/** نوار افقی ساده برای «پرتکرارها» — بدون کتابخانهٔ نمودار */
const Bars: React.FC<{ rows?: Array<{ name: string; n: number }>; color: string; label: string }> = ({ rows, color, label }) => {
  if (!rows?.length) return null;
  const max = Math.max(...rows.map((r) => r.n));
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="fac-muted" style={{ fontWeight: 700, marginBottom: 5 }}>{label}</div>
      {rows.slice(0, 6).map((r) => (
        <div key={r.name} style={{ marginBottom: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 2 }}>
            <span style={{ maxWidth: '75%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
            <b>{fa(r.n)}</b>
          </div>
          <div className="fac-bar"><i style={{ width: `${(r.n / max) * 100}%`, background: color }} /></div>
        </div>
      ))}
    </div>
  );
};

/** فهرست یافته‌ها — همهٔ بخش‌های گزارش یک شکل دارند */
interface Finding { title: React.ReactNode; badges?: React.ReactNode; lines?: Array<React.ReactNode | null | undefined | false> }
const Findings: React.FC<{ items: Finding[]; empty: string }> = ({ items, empty }) => {
  if (!items.length) return <p className="fac-note">{empty}</p>;
  return (
    <div className="fac-lines">
      {items.map((it, i) => (
        <div key={i} className="fac-line" style={{ gridTemplateColumns: '1fr' }}>
          <div>
            <div className="fac-line-title">{it.title}{it.badges}</div>
            {(it.lines || []).filter(Boolean).map((l, j) => <div key={j} className="fac-line-note">{l}</div>)}
          </div>
        </div>
      ))}
    </div>
  );
};

// ────────────────────────────── یک «کار» ──────────────────────────────

const ActionRow: React.FC<{
  a: UserbotInsightAction;
  index: number;
  busy: boolean;
  onStatus: (s: InsightActionStatus) => void;
}> = ({ a, index, busy, onStatus }) => {
  const st = STATUS_META[a.status] || STATUS_META.todo;
  const done = a.status === 'done' || a.status === 'dropped';
  return (
    <div className="fac-line" style={{ gridTemplateColumns: '34px 1fr auto', opacity: done ? 0.62 : 1 }}>
      <span className="fac-line-code" style={{ borderRadius: '50%', width: 26, height: 26, display: 'grid', placeItems: 'center', padding: 0 }}>{fa(index + 1)}</span>
      <div>
        <div className="fac-line-title" style={{ textDecoration: a.status === 'dropped' ? 'line-through' : 'none' }}>{a.title}</div>
        {a.why && <div className="fac-line-note">{a.why}</div>}
        <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <Tag label={CATEGORY_FA[a.category || 'site'] || a.category} />
          <Pill label={`اثر: ${PRIORITY_FA[a.impact || 'medium'].label}`} color={PRIORITY_FA[a.impact || 'medium'].color} />
          <Tag label={`تلاش: ${PRIORITY_FA[a.effort || 'medium'].label}`} />
          <Pill label={st.label} color={st.color} />
          {a.doneAt && <small className="fac-muted">{relTime(a.doneAt)}</small>}
        </div>
      </div>
      <span style={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {a.status !== 'done' && <IconAction title="انجام شد" color="success" disabled={busy} onClick={() => onStatus('done')}><CheckRoundedIcon fontSize="small" /></IconAction>}
        {a.status === 'todo' && <IconAction title="در حال انجام" color="primary" disabled={busy} onClick={() => onStatus('doing')}><HourglassBottomRoundedIcon fontSize="small" /></IconAction>}
        {a.status !== 'dropped' && a.status !== 'done' && <IconAction title="کنار بگذار" color="error" disabled={busy} onClick={() => onStatus('dropped')}><CloseRoundedIcon fontSize="small" /></IconAction>}
        {done && <IconAction title="برگردان به انجام‌نشده" disabled={busy} onClick={() => onStatus('todo')}><UndoRoundedIcon fontSize="small" /></IconAction>}
      </span>
    </div>
  );
};

// ────────────────────────────── تب ──────────────────────────────

const InsightsTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [source, setSource] = useState<InsightSource>('groups');
  const [rows, setRows] = useState<UserbotInsightRow[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [report, setReport] = useState<UserbotInsight | null>(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await userbotService.insights(1, 40, source);
      setRows(r.items);
      // اگر گزارشِ انتخاب‌شده مال منبع دیگری بود، به تازه‌ترینِ همین منبع برگرد
      setSelected((cur) => (cur && r.items.some((x) => x._id === cur) ? cur : r.items[0]?._id || null));
      if (!r.items.length) setReport(null);
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, source]);
  useEffect(() => { loadList(); }, [loadList]);

  const loadReport = useCallback(async (id: string) => {
    setReportLoading(true);
    try { setReport(await userbotService.insight(id)); }
    catch (e) { enqueueSnackbar(errMsg(e), { variant: 'error' }); }
    finally { setReportLoading(false); }
  }, [enqueueSnackbar]);
  useEffect(() => { if (selected) loadReport(selected); }, [selected, loadReport]);

  const run = async () => {
    setRunning(true);
    try {
      const r = await userbotService.runInsight(undefined, source);
      enqueueSnackbar(r.message, { variant: r.insight.status === 'ok' ? 'success' : 'warning' });
      await loadList();
      setSelected(r.insight._id);
      setReport(r.insight);
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setRunning(false);
    }
  };

  const setStatus = async (index: number, status: InsightActionStatus) => {
    if (!report) return;
    setBusy(true);
    try {
      setReport(await userbotService.setInsightAction(report._id, index, status));
      loadList();
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const st = report?.stats;
  const actions = report?.actions || [];
  const doneCount = actions.filter((a) => a.status === 'done').length;
  const openCount = actions.filter((a) => a.status === 'todo' || a.status === 'doing').length;
  const isCh = source === 'channels';
  const srcMeta = SOURCES.find((x) => x.key === source) || SOURCES[0];

  const insights = useMemo(() => {
    const out: Array<{ tone: Tone; text: React.ReactNode }> = [];
    if (!report) return out;
    if (report.status === 'failed') { out.push({ tone: 'bad', text: <>ساخت این گزارش ناموفق بود: {report.error}</> }); return out; }
    if (report.status === 'empty') { out.push({ tone: 'warn', text: isCh ? 'در این بازه پستی از کانال‌ها خوانده نشده بود؛ اول در تب کانال‌ها چند کانال را روشن کنید.' : 'در این بازه پیامی برای تحلیل نبود؛ گروه‌های فعال را بررسی کنید.' }); return out; }
    if (report.summary) out.push({ tone: 'info', text: report.summary });
    if (openCount > 0) out.push({ tone: 'warn', text: <><b>{fa(openCount)}</b> کار از {fa(actions.length)} کارِ این گزارش هنوز انجام نشده است. هر کدام را که تمام کردید همین‌جا تیک بزنید تا در تاریخچه بماند.</> });
    else if (actions.length > 0) out.push({ tone: 'good', text: <>همهٔ {fa(actions.length)} کار این گزارش رسیدگی شده است.</> });
    return out;
  }, [report, openCount, actions.length, isCh]);

  return (
    <>
      {/* انتخاب منبع */}
      <div className="fac-card" style={{ marginBottom: 12 }}>
        <div className="fac-card-body" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '10px 14px' }}>
          {SOURCES.map((x) => (
            <button key={x.key} type="button" className={`fac-tab ${source === x.key ? 'active' : ''}`} style={{ minWidth: 0 }} onClick={() => setSource(x.key)}>
              {x.icon}{x.label}
            </button>
          ))}
          <span className="fac-muted">{srcMeta.hint}</span>
        </div>
      </div>

      <Insights items={insights} />

      <div className="ubc-split" style={{ gridTemplateColumns: 'minmax(0, 280px) minmax(0, 1fr)' }}>
        {/* تاریخچه */}
        <SectionCard
          icon={<InsightsRoundedIcon />}
          title="تاریخچه"
          hint="هر شب یک گزارش؛ پاک نمی‌شوند"
          actions={<IconAction title="به‌روزرسانی" onClick={loadList} disabled={loading}><RefreshIcon /></IconAction>}
        >
          <Button
            fullWidth
            variant="contained"
            size="small"
            startIcon={running ? <CircularProgress size={14} color="inherit" /> : <PlayArrowRoundedIcon />}
            disabled={running}
            onClick={run}
            sx={{ mb: 1.5 }}
          >
            {isCh ? 'تحلیل کانال‌های امروز' : 'تحلیل گروه‌های امروز'}
          </Button>
          {loading && rows.length === 0 ? (
            <Loading size={20} />
          ) : rows.length === 0 ? (
            <EmptyHint
              icon={<InsightsRoundedIcon />}
              title="هنوز گزارشی نیست"
              hint={isCh
                ? 'اول در تب «کانال‌ها» چند کانال را روشن کنید. هر شب ساعت ۲۳ به وقت تهران گزارش خودکار ساخته می‌شود.'
                : 'هر شب ساعت ۲۳ به وقت تهران خودکار ساخته می‌شود، یا همین حالا دکمهٔ بالا را بزنید.'}
            />
          ) : (
            <div className="ubc-scroll" style={{ maxHeight: 'calc(100vh - 460px)' }}>
              {rows.map((r) => (
                <div key={r._id} className={`ubc-pick ${selected === r._id ? 'on' : ''}`} onClick={() => setSelected(r._id)}>
                  <div className="ubc-pick-main">
                    <b style={{ direction: 'ltr', textAlign: 'right' }}>{fa(r.day)}</b>
                    <small>{fa(r.stats?.messages ?? 0)} {isCh ? 'پست' : 'پیام'} · {r.scope === 'manual' ? 'دستی' : 'خودکار'}</small>
                  </div>
                  {r.status === 'ok'
                    ? <Tag color={r.actionsDone >= r.actionsTotal && r.actionsTotal > 0 ? 'success' : 'default'} label={`${fa(r.actionsDone)}/${fa(r.actionsTotal)}`} />
                    : <Tag color={r.status === 'failed' ? 'error' : 'warning'} label={r.status === 'failed' ? 'ناموفق' : 'خالی'} />}
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* گزارش */}
        <div>
          {reportLoading && !report ? (
            <SectionCard title="گزارش"><Loading /></SectionCard>
          ) : !report ? (
            <SectionCard title="گزارش"><EmptyHint icon={<InsightsRoundedIcon />} title="گزارشی انتخاب نشده" hint="از ستون کنار یک روز را انتخاب کنید." /></SectionCard>
          ) : (
            <>
              {/* آمار دقیق (بدون AI) */}
              {st && (
                <Kpis items={(isCh
                  ? [
                      { label: 'پست تحلیل‌شده', value: st.messages, count: true, color: 'blue' as const, help: 'همهٔ پست‌های کانال‌های روشن در این روز' },
                      { label: 'کانال‌ها', value: st.groups, count: true, color: 'blue' as const },
                      { label: 'خبر', value: st.news ?? 0, count: true, color: 'purple' as const },
                      { label: 'تخفیف / کمپین', value: st.promos ?? 0, count: true, color: 'orange' as const },
                      { label: 'پست قیمت‌دار', value: st.priceMentions ?? 0, count: true, color: 'green' as const, help: 'پست‌هایی که عدد قیمت داشتند' },
                      { label: 'کار انجام‌شده', raw: `${fa(doneCount)}/${fa(actions.length)}`, value: null, color: (doneCount >= actions.length && actions.length ? 'green' : 'orange') as 'green' | 'orange' },
                    ]
                  : [
                      { label: 'پیام تحلیل‌شده', value: st.messages, count: true, color: 'blue' as const, help: 'هر پیامی که نشانهٔ بازار داشت — حتی آن‌هایی که جواب نگرفتند' },
                      { label: 'افراد یکتا', value: st.people, count: true, color: 'purple' as const },
                      { label: 'گروه‌ها', value: st.groups, count: true, color: 'blue' as const },
                      { label: 'سؤال', value: st.questions, count: true, color: 'orange' as const },
                      { label: 'آگهی فروشنده', value: st.ads, count: true, color: 'gray' as const, help: 'آگهی‌ها جواب نمی‌گیرند ولی نشانهٔ عرضهٔ بازارند' },
                      { label: 'کار انجام‌شده', raw: `${fa(doneCount)}/${fa(actions.length)}`, value: null, color: (doneCount >= actions.length && actions.length ? 'green' : 'orange') as 'green' | 'orange' },
                    ]
                )} />
              )}

              {report.status === 'failed' && <Alert severity="error" sx={{ mb: 1.5 }}>{report.error}</Alert>}

              {/* کارها */}
              {actions.length > 0 && (
                <SectionCard
                  icon={<CheckRoundedIcon />}
                  title={`کارهایی که باید انجام شود — گزارش ${fa(report.day)}`}
                  hint={`${report.scope === 'manual' ? 'اجرای دستی' : 'خودکار'} · ${faDate(report.createdAt)}${report.aiModel ? ` · ${report.aiModel}` : ''}${report.costUsd ? ` · ${fa(report.costUsd.toFixed(4))}$` : ''}`}
                >
                  <div className="fac-lines">
                    {actions.map((a, i) => <ActionRow key={i} a={a} index={i} busy={busy} onStatus={(s) => setStatus(i, s)} />)}
                  </div>
                </SectionCard>
              )}

              {!isCh && (
                <>
                  <SectionCard icon={<TrendingUpRoundedIcon />} title="مردم دنبال چه بودند" hint="تقاضای واقعی، با شاهدش">
                    <Findings
                      empty="موردی ثبت نشد."
                      items={report.demand.map((d) => ({
                        title: d.item,
                        badges: d.volume ? <Tag color="secondary" label={d.volume} /> : null,
                        lines: [d.signal && `شاهد: ${d.signal}`, d.note],
                      }))}
                    />
                  </SectionCard>

                  <SectionCard icon={<TrendingUpRoundedIcon />} title="پرتکرارها" hint="شمارش دقیق و بدون هوش مصنوعی">
                    <Bars label="محصولات" rows={st?.topObjects} color="var(--color-primary)" />
                    <Bars label="برندها" rows={st?.topBrands} color="#9333ea" />
                    <Bars label="دغدغه‌های پوست/مو" rows={st?.topConcerns} color="var(--color-warning)" />
                    <Bars label="نوع پوست/مو" rows={st?.topSkinTypes} color="var(--color-success)" />
                    {st && (
                      <>
                        <SubHead>نیت‌ها</SubHead>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {Object.entries(st.byIntent || {}).filter(([k]) => k !== 'none').sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                            <Tag key={k} label={`${INTENT_FA[k] || k}: ${fa(n)}`} />
                          ))}
                        </div>
                      </>
                    )}
                  </SectionCard>

                  <div className="ubc-split" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <SectionCard icon={<ShoppingBagRoundedIcon />} title="محصولی که باید اضافه شود">
                      <Findings
                        empty="پیشنهادی نبود."
                        items={report.products.map((p) => ({
                          title: p.name,
                          badges: <Pill label={PRIORITY_FA[p.priority || 'medium'].label} color={PRIORITY_FA[p.priority || 'medium'].color} />,
                          lines: [p.why],
                        }))}
                      />
                    </SectionCard>
                    <SectionCard icon={<ReportProblemRoundedIcon />} title="مشکل و ترس کاربرها">
                      <Findings
                        empty="موردی ثبت نشد."
                        items={report.problems.map((p) => ({
                          title: p.problem,
                          lines: [p.impact && `اثر: ${p.impact}`, p.fix && <span className="fac-pos">راه‌حل: {p.fix}</span>],
                        }))}
                      />
                    </SectionCard>
                  </div>

                  <div className="ubc-split" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <SectionCard icon={<CampaignRoundedIcon />} title="ایده‌های جذب کاربر">
                      <Findings empty="ایده‌ای ثبت نشد." items={report.marketing.map((m) => ({ title: m.idea, lines: [m.why] }))} />
                    </SectionCard>
                    <SectionCard icon={<FormatQuoteRoundedIcon />} title="نقل‌قول از گروه‌ها" hint="شاهدِ واقعی، نه تفسیر">
                      <Findings empty="نقل‌قولی ثبت نشد." items={report.quotes.map((q) => ({ title: <span style={{ fontWeight: 500 }}>«{q.text}»</span>, lines: [q.group && `— ${q.group}`] }))} />
                    </SectionCard>
                  </div>
                </>
              )}

              {isCh && (
                <>
                  <div className="ubc-split" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <SectionCard icon={<NewspaperRoundedIcon />} title="خبرهایی که برای ما مهم‌اند">
                      <Findings empty="خبر مهمی نبود." items={report.news.map((x) => ({ title: x.title, badges: x.source ? <Tag label={x.source} /> : null, lines: [x.why] }))} />
                    </SectionCard>
                    <SectionCard icon={<StorefrontRoundedIcon />} title="رقبا چه کردند">
                      <Findings
                        empty="حرکتی از رقبا ثبت نشد."
                        items={report.competitors.map((x) => ({
                          title: x.name,
                          badges: x.price ? <Pill label={x.price} color={UB.ok} /> : null,
                          lines: [x.what, x.note && <span className="fac-warn">برای ما: {x.note}</span>],
                        }))}
                      />
                    </SectionCard>
                  </div>

                  <SectionCard icon={<SellRoundedIcon />} title="قیمت بازار" hint="از متن پست‌ها استخراج شده؛ قبل از تغییر قیمت خودتان یک بار چشمی بررسی کنید">
                    <Findings
                      empty="قیمتی استخراج نشد."
                      items={report.pricing.map((x) => ({
                        title: x.item,
                        badges: <>{x.price && <Pill label={x.price} color={UB.ok} />}{x.where && <Tag label={x.where} />}</>,
                        lines: [x.note],
                      }))}
                    />
                  </SectionCard>

                  <SectionCard icon={<TrendingUpRoundedIcon />} title="پرتکرارها" hint="شمارش دقیق و بدون هوش مصنوعی">
                    <Bars label="بازه‌های قیمتی" rows={st?.priceBands} color="var(--color-success)" />
                    <Bars label="محصولات" rows={st?.topObjects} color="var(--color-primary)" />
                    <Bars label="برندها" rows={st?.topBrands} color="#9333ea" />
                    <Bars label="دغدغه‌های پوست/مو" rows={st?.topConcerns} color="var(--color-info)" />
                    <Bars label="پرکارترین کانال‌ها" rows={st?.topChannels} color="var(--color-warning)" />
                    {!!st?.avgViews && <p className="fac-note">میانگین بازدید هر پست: {fa(st.avgViews)}</p>}
                  </SectionCard>

                  <div className="ubc-split" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <SectionCard icon={<ShoppingBagRoundedIcon />} title="محصولی که باید اضافه شود">
                      <Findings
                        empty="پیشنهادی نبود."
                        items={report.products.map((p) => ({
                          title: p.name,
                          badges: <Pill label={PRIORITY_FA[p.priority || 'medium'].label} color={PRIORITY_FA[p.priority || 'medium'].color} />,
                          lines: [p.why],
                        }))}
                      />
                    </SectionCard>
                    <SectionCard icon={<EditNoteRoundedIcon />} title="محتوایی که باید نوشت">
                      <Findings empty="ایده‌ای ثبت نشد." items={report.content.map((x) => ({ title: x.idea, lines: [x.why] }))} />
                    </SectionCard>
                  </div>

                  <SectionCard icon={<FormatQuoteRoundedIcon />} title="از پست‌ها" hint="شاهدِ واقعی، نه تفسیر">
                    <Findings empty="نقل‌قولی ثبت نشد." items={report.quotes.map((q) => ({ title: <span style={{ fontWeight: 500 }}>«{q.text}»</span>, lines: [q.group && `— ${q.group}`] }))} />
                  </SectionCard>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default InsightsTab;
